import { useCallback, useState } from "react";
import { Link } from "expo-router";
import { GroupCard } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { View, XStack } from "tamagui";

import type { RouterOutputs } from "~/utils/api";
import { api } from "~/utils/api";
import { chunkArray } from "~/utils/chunk-array";
import { dateToDDMMYY } from "~/utils/date-to-readable";
import { useGroupsFilter } from "~/components/blocks/filters/groups-filter";
import { Pagination } from "~/components/blocks/pagination";
import type { Action as DropdownAction } from "./group-options-popover";
import { GroupOptionsPopover } from "./group-options-popover";
import { RenameGroupDialog } from "./rename-group";

const LIMIT = 50;
const GAP_BETWEEN_GROUPS = 16;

const resolveGroupCardWidth = (width: number) => {
  return Math.floor(width / 2 - GAP_BETWEEN_GROUPS / 2);
};

export const GroupsGrid = () => {
  const { filter, dispatch } = useGroupsFilter();
  const [groupRowWidth, setGroupRowWidth] = useState(0);
  const isSearchEmpty = filter.search === "" || filter.search === undefined;

  const [data] = api.group.list.useSuspenseQuery(
    {
      page: filter.page,
      limit: LIMIT,
      search: filter.search,
    },
    {
      select: (data) => {
        return {
          totalPages: data.totalPages,
          rows: chunkArray(data.rows, 2),
        };
      },
    },
  );

  return (
    <View gap={"$8"}>
      <View gap={"$2"} data-id="group-grid-col" alignSelf="stretch" flex={1}>
        {data?.rows.map((item, i) => (
          <XStack
            data-id="group-grid-row"
            key={i}
            gap={"$2"}
            onLayout={(e) => setGroupRowWidth(e.nativeEvent.layout.width)}
            flex={2}
          >
            {item.map((group) => (
              <GroupItem
                group={group}
                groupRowWidth={groupRowWidth}
                key={group?.id}
              />
            ))}
          </XStack>
        ))}
      </View>
      {isSearchEmpty && data.totalPages > 1 ? (
        <Pagination
          currentPage={filter.page}
          onPageChange={(page) =>
            dispatch({
              type: "SET_FILTER",
              filterName: "page",
              value: page,
            })
          }
          totalPages={data.totalPages}
        />
      ) : null}
    </View>
  );
};

type GroupItemType = RouterOutputs["group"]["list"]["rows"][number];

const GroupItem: React.FC<{ group: GroupItemType; groupRowWidth: number }> = ({
  group,
  groupRowWidth,
}) => {
  const { t } = useTranslation("groups");
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const utils = api.useUtils();

  const { mutate: deactivate } = api.group.deactivate.useMutation({
    onSuccess: () => utils.group.list.invalidate(),
  });

  const { mutate: activate } = api.group.activate.useMutation({
    onSuccess: () => utils.group.list.invalidate(),
  });

  const { mutate: deleteGroup } = api.group.delete.useMutation({
    onSuccess: () => utils.group.list.invalidate(),
  });

  const handleActionPress = useCallback(
    (actions: DropdownAction) => {
      switch (actions) {
        case "edit":
          setOpenRenameDialog(true);
          break;
        case "delete":
          deleteGroup({ groupId: group.id });
          break;
        case "deactivate":
          deactivate({ groupId: group.id });
          break;
        case "activate":
          activate({ groupId: group.id });
          break;
      }
    },
    [activate, deactivate, group, deleteGroup, setOpenRenameDialog],
  );

  return (
    <>
      <RenameGroupDialog
        groupId={group.id}
        open={openRenameDialog}
        setOpen={setOpenRenameDialog}
      />
      <Link
        data-id="group-item-link"
        href={`/groups/management/${group.id}`}
        asChild
      >
        <GroupCard.Body
          data-id="group-item-body"
          cursor={"pointer"}
          disabled={!group.active}
          maxWidth={resolveGroupCardWidth(groupRowWidth)}
          flex={1}
          gap={"$3"}
        >
          <XStack justifyContent="space-between" flex={1}>
            <View gap={"$1"} flex={1}>
              {group?.createdAt ? (
                <GroupCard.Label data-id="group-item-created-at">
                  {t("group-item.created-at", {
                    date: dateToDDMMYY(group.createdAt),
                  })}
                </GroupCard.Label>
              ) : null}
              <GroupCard.Title data-id="group-item-title">
                {group.name}
              </GroupCard.Title>
            </View>
            <XStack gap={"$1"} flex={1} maxWidth={"$4.1"}>
              {group.id !== "organization" ? (
                <GroupOptionsPopover
                  isGroupActive={group.active}
                  isNotGroupRemovable={
                    group.hasSlices || (group.counts?.total ?? 0) > 0
                  }
                  onActionPress={handleActionPress}
                  trigger={
                    <GroupCard.Button>
                      <GroupCard.EllipsisIcon />
                    </GroupCard.Button>
                  }
                />
              ) : null}
            </XStack>
          </XStack>
          <XStack flex={2} gap={"$1"}>
            <GroupCard.StatCard data-id="group-item-requested" width={"50%"}>
              <GroupCard.Score data-id="group-item-requested-count">
                {group.counts.total}
              </GroupCard.Score>
              <GroupCard.StatDescription data-id="group-item-requested-label">
                {t("group-item.invited-count-title")}
              </GroupCard.StatDescription>
            </GroupCard.StatCard>

            <GroupCard.StatCard data-id="group-item-registered" width={"50%"}>
              <GroupCard.ScoreContainer>
                <GroupCard.Score data-id="group-item-registered-count">
                  {group.counts.registered}
                </GroupCard.Score>
              </GroupCard.ScoreContainer>
              <GroupCard.StatDescription data-id="group-item-registered-label">
                {t("group-item.registered-count-title")}
              </GroupCard.StatDescription>
            </GroupCard.StatCard>
          </XStack>
        </GroupCard.Body>
      </Link>
    </>
  );
};
